/**
 * Gamification Service v2.0
 * 
 * Complete gamification system with Firebase persistence:
 * - XP & Leveling System
 * - Streak Management
 * - Achievement Tracking
 * - Daily Challenges
 * - Milestone Celebrations
 * 
 * Syncs with Firebase for cross-device persistence
 */

import {
    LEVEL_CONFIGS,
    ACHIEVEMENTS,
    DAILY_CHALLENGES,
    PROGRESS_MILESTONES,
    DEFAULT_GAMIFICATION_CONFIG,
    calculateLevel,
    xpToNextLevel,
    getRandomDailyChallenges,
    DailyChallenge,
    Achievement,
    ProgressMilestone,
    LevelConfig
} from '../config/gamificationConfig';
import { CPALE_SYLLABUS } from '../config/masteryConfig';
import { QuizAttempt, MockExamAttempt } from '../types';
import { db } from './firebase/config';
import { doc, updateDoc, getDoc, arrayUnion, increment } from 'firebase/firestore';
import { publishActivity } from './socialService';

// ==================== TYPES ====================

export interface UserGamificationState {
    // XP & Level
    totalXP: number;
    currentStreak: number;
    longestStreak: number;
    lastActiveDate: string; // ISO date string (YYYY-MM-DD)
    streakStatus: 'active' | 'frozen' | 'broken'; // New: Track status
    frozenDate: string | null; // New: When it froze

    // Weekly Leaderboard
    weeklyXP: number;
    currentWeekId: string; // e.g. "2024-W05"

    // Achievements
    unlockedAchievements: string[];
    unlockedMilestones: string[];

    // Daily Activity
    dailyProgress: {
        date: string;
        questionsAsked: number;
        quizzesCompleted: number;
        studyMinutes: number;
        domainsStudied: string[];
        sessionsStarted: number;
    };

    // Daily Challenges
    dailyChallenges: {
        date: string;
        challenges: DailyChallenge[];
        progress: Record<string, number>;
        completedIds: string[];
    };

    // Statistics
    stats: {
        totalQuestions: number;
        totalQuizzes: number;
        totalStudyMinutes: number;
        totalSessions: number;
        quizzesPassed: number;
        perfectQuizzes: number;
        domainQuestions: Record<string, number>;
    };

    // Old Mastery Mode (Deprecated but kept for compat)
    masteryProgress?: {
        unlockedNodes: string[];
        completedNodes: string[];
        nodeScores: Record<string, number>;
        currentNodeId: string;
    };

    // CPALE Mastery System (New)
    selectedSubject?: string | null; // 'far', 'aud', etc.
    subjectProgress?: Record<string, {
        isPreliminaryPassed: boolean;
        currentTopicId: string;
        topicXP: Record<string, number>; // TopicID -> XP (legacy)
        completedTopics: string[];
        // Checklist-based progression
        checklistProgress?: Record<string, string[]>; // TopicID -> completed checklist item IDs
        topicNotes?: Record<string, { content: string; savedAt: number }>; // TopicID -> written note
        examAttempts?: Record<string, number>; // TopicID -> attempt count
        remedialChecklist?: Record<string, {
            attemptNumber: number;
            items: string[];
            completedItems: string[];
            wrongQuestions: Array<{ question: string; correctAnswer: string; userAnswer: string }>;
        }>;
    }>;

    // Study Guide Progress (tracks completed phases per topic)
    guideProgress?: Record<string, string[]>; // topicId -> completed phase IDs

    // Timestamps
    lastUpdated: number;
    createdAt: number;
}

export interface XPGainResult {
    xpGained: number;
    previousXP: number;
    newTotalXP: number;
    leveledUp: boolean;
    previousLevel: LevelConfig;
    newLevel: LevelConfig;
    bonusXP: number;
    reason: string;
    levelUpReward?: {
        tokens: number;
        unlocks: string[];
    };
}

export interface AchievementUnlock {
    achievement: Achievement;
    xpAwarded: number;
    tokensAwarded: number;
    unlockedAt: number;
}

export interface StreakResult {
    currentStreak: number;
    previousStreak: number;
    longestStreak: number;
    isNewDay: boolean;
    streakExtended: boolean;
    streakLost: boolean;
    streakFrozen: boolean; // New
    bonusTokens: number;
}

export interface DailyChallengeStatus {
    challenge: DailyChallenge;
    progress: number;
    completed: boolean;
    claimedReward: boolean;
}

export interface MilestoneResult {
    milestone: ProgressMilestone;
    justReached: boolean;
    tokensAwarded: number;
    xpAwarded: number;
}

// ==================== LOCAL STORAGE KEYS ====================

const STORAGE_KEY = 'luca_gamification_state';
const LAST_SYNC_KEY = 'luca_gamification_last_sync';

// ==================== STATE MANAGEMENT ====================

let currentState: UserGamificationState | null = null;
let syncPending = false;
const listeners: ((state: UserGamificationState) => void)[] = [];

/**
 * Subscribe to gamification state changes
 * Returns unsubscribe function
 */
export function subscribeToGamification(callback: (state: UserGamificationState) => void): () => void {
    listeners.push(callback);
    // Immediately call with current state if available
    if (currentState) {
        callback(currentState);
    }

    return () => {
        const index = listeners.indexOf(callback);
        if (index > -1) {
            listeners.splice(index, 1);
        }
    };
}

/**
 * Notify all listeners of state change
 */
function notifyListeners(state: UserGamificationState): void {
    listeners.forEach(listener => {
        try {
            listener(state);
        } catch (e) {
            console.error('[Gamification] Error in listener:', e);
        }
    });
}

/**
 * Get default initial state
 */
function getDefaultState(): UserGamificationState {
    const today = new Date().toISOString().split('T')[0];
    const now = Date.now();

    // Generate initial daily challenges
    const initialChallenges = getRandomDailyChallenges(3).map(c => ({
        ...c
    }));

    return {
        totalXP: 0,
        currentStreak: 0,
        longestStreak: 0,
        lastActiveDate: today,
        streakStatus: 'active',
        frozenDate: null,
        weeklyXP: 0,
        currentWeekId: getWeekId(),
        unlockedAchievements: [],
        unlockedMilestones: [],
        dailyProgress: {
            date: today,
            questionsAsked: 0,
            quizzesCompleted: 0,
            studyMinutes: 0,
            domainsStudied: [],
            sessionsStarted: 0
        },
        dailyChallenges: {
            date: today,
            challenges: initialChallenges,
            progress: {},
            completedIds: []
        },
        stats: {
            totalQuestions: 0,
            totalQuizzes: 0,
            totalStudyMinutes: 0,
            totalSessions: 0,
            quizzesPassed: 0,
            perfectQuizzes: 0,
            domainQuestions: {}
        },
        masteryProgress: {
            unlockedNodes: ['far-1'], // Start with first node
            completedNodes: [],
            nodeScores: {},
            currentNodeId: 'far-1'
        },
        // CPALE Defaults
        selectedSubject: null,
        subjectProgress: {},
        lastUpdated: now,
        createdAt: now
    };
}

/**
 * Load gamification state from localStorage
 */
export function loadGamificationState(): UserGamificationState {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
            const parsed = JSON.parse(stored);
            currentState = validateAndMigrateState(parsed);
            return currentState;
        }
    } catch (e) {
        console.error('[Gamification] Error loading state:', e);
    }

    currentState = getDefaultState();
    saveGamificationState(currentState);
    return currentState;
}

/**
 * Validate and migrate state to latest schema
 */
function validateAndMigrateState(state: any): UserGamificationState {
    const defaults = getDefaultState();
    const today = new Date().toISOString().split('T')[0];

    // Merge with defaults to ensure all fields exist
    const migrated: UserGamificationState = {
        totalXP: state.totalXP ?? defaults.totalXP,
        currentStreak: state.currentStreak ?? defaults.currentStreak,
        longestStreak: state.longestStreak ?? defaults.longestStreak,
        lastActiveDate: state.lastActiveDate ?? defaults.lastActiveDate,
        streakStatus: state.streakStatus ?? 'active',
        frozenDate: state.frozenDate ?? null,
        weeklyXP: state.weeklyXP ?? 0,
        currentWeekId: state.currentWeekId ?? getWeekId(),
        unlockedAchievements: state.unlockedAchievements ?? defaults.unlockedAchievements,
        unlockedMilestones: state.unlockedMilestones ?? defaults.unlockedMilestones,
        dailyProgress: {
            ...defaults.dailyProgress,
            ...state.dailyProgress
        },
        dailyChallenges: {
            ...defaults.dailyChallenges,
            ...state.dailyChallenges
        },
        stats: {
            ...defaults.stats,
            ...state.stats
        },
        lastUpdated: state.lastUpdated ?? Date.now(),
        createdAt: state.createdAt ?? Date.now()
    };

    // Reset daily progress if it's a new day
    if (migrated.dailyProgress.date !== today) {
        migrated.dailyProgress = {
            date: today,
            questionsAsked: 0,
            quizzesCompleted: 0,
            studyMinutes: 0,
            domainsStudied: [],
            sessionsStarted: 0
        };
    }

    // Reset daily challenges if it's a new day
    if (migrated.dailyChallenges.date !== today) {
        const newChallenges = getRandomDailyChallenges(3);
        migrated.dailyChallenges = {
            date: today,
            challenges: newChallenges,
            progress: {},
            completedIds: []
        };
    }

    // Migration: Add masteryProgress if missing (Phase 3)
    if (!(state as any).masteryProgress) {
        migrated.masteryProgress = {
            unlockedNodes: ['far-1'],
            completedNodes: [],
            nodeScores: {},
            currentNodeId: 'far-1'
        };
    } else {
        migrated.masteryProgress = (state as any).masteryProgress;
    }

    // Migration: CPALE Mastery
    if (!(state as any).subjectProgress) {
        migrated.selectedSubject = null;
        migrated.subjectProgress = {};
    } else {
        migrated.selectedSubject = state.selectedSubject;
        migrated.subjectProgress = state.subjectProgress;
    }

    // Migration: Guide Progress
    migrated.guideProgress = state.guideProgress || {};

    // Migration: Ensure currentWeekId (Phase 3.2)
    if (!migrated.currentWeekId) {
        migrated.currentWeekId = getWeekId();
        migrated.weeklyXP = 0; // Reset if new system
    }

    return migrated;
}

/**
 * Get current week ID (e.g. "2024-W05")
 */
function getWeekId(): string {
    const now = new Date();
    const startOfYear = new Date(now.getFullYear(), 0, 1);
    const pastDays = (now.getTime() - startOfYear.getTime()) / 86400000;
    const weekNum = Math.ceil((pastDays + startOfYear.getDay() + 1) / 7);
    return `${now.getFullYear()}-W${weekNum}`;
}

/**
 * Save gamification state to localStorage and Firebase
 */
export async function saveGamificationState(state: UserGamificationState): Promise<void> {
    currentState = state;
    state.lastUpdated = Date.now();

    // Notify listeners immediately
    notifyListeners(state);

    // Save to localStorage immediately
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
        console.error('[Gamification] Error saving to localStorage:', e);
    }

    // Sync to Firebase (debounced)
    schedulFirebaseSync();
}

/**
 * Schedule Firebase sync (debounced to prevent excessive writes)
 */
let syncTimeout: NodeJS.Timeout | null = null;

function schedulFirebaseSync(): void {
    if (syncTimeout) {
        clearTimeout(syncTimeout);
    }

    syncPending = true;
    syncTimeout = setTimeout(async () => {
        await syncToFirebase();
    }, 2000); // 2 second debounce
}

/**
 * Sync gamification state to Firebase
 */
async function syncToFirebase(): Promise<void> {
    if (!currentState || !db) {
        return;
    }

    try {
        // Get current user ID from localStorage
        const storedUser = localStorage.getItem('ledger_current_user');
        if (!storedUser) return;

        const user = JSON.parse(storedUser);
        if (!user.id || user.isAnonymous) return;

        const userRef = doc(db, 'users', user.id);

        await updateDoc(userRef, {
            gamificationState: currentState,
            totalXP: currentState.totalXP,
            weeklyXP: currentState.weeklyXP || 0,
            level: calculateLevel(currentState.totalXP).level,
            streak: currentState.currentStreak,
            lastGamificationSync: Date.now()
        });

        localStorage.setItem(LAST_SYNC_KEY, Date.now().toString());
        syncPending = false;
        console.log('[Gamification] Synced to Firebase successfully');
    } catch (e) {
        console.error('[Gamification] Error syncing to Firebase:', e);
    }
}

/**
 * Load gamification state from Firebase (for sync on login)
 */
export async function loadFromFirebase(userId: string): Promise<UserGamificationState | null> {
    if (!db) {
        return null;
    }

    try {
        const userRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userRef);

        if (userDoc.exists()) {
            const data = userDoc.data();
            if (data.gamificationState) {
                return validateAndMigrateState(data.gamificationState);
            }
        }
    } catch (e) {
        console.error('[Gamification] Error loading from Firebase:', e);
    }

    return null;
}

/**
 * Initialize gamification for a user (sync with Firebase)
 */
export async function initializeGamification(userId: string): Promise<void> {
    const localState = loadGamificationState();
    const remoteState = await loadFromFirebase(userId);

    if (remoteState) {
        const merged = mergeStates(localState, remoteState);
        await saveGamificationState(merged);
        console.log('[Gamification] Initialized and synced with Firebase');
    } else {
        // No remote state, push local
        await saveGamificationState(localState);
        console.log('[Gamification] Initialized new remote state');
    }
}

/**
 * Merge local and remote state (use higher values)
 */
export function mergeStates(local: UserGamificationState, remote: UserGamificationState): UserGamificationState {
    const useLocalWeek = (local.currentWeekId || '') > (remote.currentWeekId || '') || ((local.currentWeekId || '') === (remote.currentWeekId || '') && (local.weeklyXP || 0) > (remote.weeklyXP || 0));

    return {
        totalXP: Math.max(local.totalXP, remote.totalXP),
        weeklyXP: useLocalWeek ? (local.weeklyXP || 0) : (remote.weeklyXP || 0),
        currentWeekId: useLocalWeek ? (local.currentWeekId || getWeekId()) : (remote.currentWeekId || getWeekId()),
        currentStreak: Math.max(local.currentStreak, remote.currentStreak),
        longestStreak: Math.max(local.longestStreak, remote.longestStreak),
        lastActiveDate: local.lastUpdated > remote.lastUpdated ? local.lastActiveDate : remote.lastActiveDate,
        streakStatus: local.lastUpdated > remote.lastUpdated ? local.streakStatus : remote.streakStatus,
        frozenDate: local.lastUpdated > remote.lastUpdated ? local.frozenDate : remote.frozenDate,
        unlockedAchievements: [...new Set([...local.unlockedAchievements, ...remote.unlockedAchievements])],
        unlockedMilestones: [...new Set([...local.unlockedMilestones, ...remote.unlockedMilestones])],
        dailyProgress: local.lastUpdated > remote.lastUpdated ? local.dailyProgress : remote.dailyProgress,
        dailyChallenges: local.lastUpdated > remote.lastUpdated ? local.dailyChallenges : remote.dailyChallenges,
        stats: {
            totalQuestions: Math.max(local.stats.totalQuestions, remote.stats.totalQuestions),
            totalQuizzes: Math.max(local.stats.totalQuizzes, remote.stats.totalQuizzes),
            totalStudyMinutes: Math.max(local.stats.totalStudyMinutes, remote.stats.totalStudyMinutes),
            totalSessions: Math.max(local.stats.totalSessions, remote.stats.totalSessions),
            quizzesPassed: Math.max(local.stats.quizzesPassed, remote.stats.quizzesPassed),
            perfectQuizzes: Math.max(local.stats.perfectQuizzes, remote.stats.perfectQuizzes),
            domainQuestions: mergeDomainQuestions(local.stats.domainQuestions, remote.stats.domainQuestions)
        },
        lastUpdated: Math.max(local.lastUpdated, remote.lastUpdated),
        createdAt: Math.min(local.createdAt, remote.createdAt)
    };
}

function mergeDomainQuestions(local: Record<string, number>, remote: Record<string, number>): Record<string, number> {
    const merged = { ...remote };
    for (const [domain, count] of Object.entries(local)) {
        merged[domain] = Math.max(merged[domain] || 0, count);
    }
    return merged;
}

// ==================== XP & LEVELING ====================

/**
 * Award XP to the user
 */
export function awardXP(amount: number, reason: string): XPGainResult {
    const state = loadGamificationState();
    const previousXP = state.totalXP;
    const previousLevel = calculateLevel(previousXP);

    // Apply streak bonus
    let bonusXP = 0;
    if (state.currentStreak > 0) {
        bonusXP = Math.floor(amount * (state.currentStreak * 0.05)); // 5% per streak day
        bonusXP = Math.min(bonusXP, amount); // Cap at 100% bonus
    }

    const totalGained = amount + bonusXP;
    const newTotalXP = previousXP + totalGained;
    state.totalXP = newTotalXP;

    // Weekly XP Logic
    const thisWeekId = getWeekId();
    if (state.currentWeekId !== thisWeekId) {
        state.currentWeekId = thisWeekId;
        state.weeklyXP = 0;
    }
    state.weeklyXP = (state.weeklyXP || 0) + totalGained;

    const newLevel = calculateLevel(newTotalXP);
    const leveledUp = newLevel.level > previousLevel.level;

    // Process level-up rewards
    let levelUpReward: { tokens: number; unlocks: string[] } | undefined;
    if (leveledUp) {
        levelUpReward = { tokens: newLevel.tokenBonus, unlocks: [] };

        // Grant token bonus for leveling up
        if (newLevel.tokenBonus > 0) {
            try {
                const storedUser = localStorage.getItem('ledger_current_user');
                if (storedUser) {
                    const user = JSON.parse(storedUser);
                    user.tokenBalance = (user.tokenBalance || 0) + newLevel.tokenBonus;

                    // Check for background unlocks in level perks
                    const bgUnlockPerks = newLevel.perks.filter(p => p.startsWith('Unlock:'));
                    const bgIdMap: Record<string, string> = {
                        'Unlock: Midnight Forest bg': 'bg-midnight-forest',
                        'Unlock: Synthwave Grid bg': 'bg-synthwave',
                        'Unlock: Aurora Borealis bg': 'bg-aurora',
                        'Unlock: Royal Velvet bg': 'bg-royal',
                        'Unlock: Cherry Blossom bg': 'bg-cherry-blossom',
                        'Unlock: Golden Noir bg': 'bg-golden',
                        'Unlock: Cosmic Nebula bg': 'bg-cosmic-nebula',
                        'Unlock: Obsidian Flow bg': 'bg-obsidian',
                    };

                    for (const perk of bgUnlockPerks) {
                        const bgId = bgIdMap[perk];
                        if (bgId) {
                            const unlocked = user.unlockedBackgrounds || [];
                            if (!unlocked.includes(bgId)) {
                                unlocked.push(bgId);
                                user.unlockedBackgrounds = unlocked;
                                levelUpReward.unlocks.push(bgId);
                            }
                        }
                    }

                    // Level 20 special: unlock all backgrounds
                    if (newLevel.level >= 20) {
                        const allBgIds = ['bg-aurora', 'bg-holographic', 'bg-synthwave', 'bg-royal', 'bg-golden', 'bg-midnight-forest', 'bg-obsidian', 'bg-matrix', 'bg-cherry-blossom', 'bg-cosmic-nebula', 'bg-ocean-depth', 'bg-sunset-gradient', 'bg-paper-texture', 'bg-minimal-dots', 'bg-blueprint', 'bg-lavender-dream', 'bg-emerald-glass', 'bg-rose-gold'];
                        const unlocked = user.unlockedBackgrounds || [];
                        for (const bgId of allBgIds) {
                            if (!unlocked.includes(bgId)) {
                                unlocked.push(bgId);
                                levelUpReward.unlocks.push(bgId);
                            }
                        }
                        user.unlockedBackgrounds = unlocked;
                    }

                    localStorage.setItem('ledger_current_user', JSON.stringify(user));

                    // Sync background unlocks to Firebase
                    if (levelUpReward.unlocks.length > 0 || newLevel.tokenBonus > 0) {
                        try {
                            const userRef = doc(db, 'users', user.id);
                            updateDoc(userRef, {
                                tokenBalance: user.tokenBalance,
                                unlockedBackgrounds: user.unlockedBackgrounds || [],
                            }).catch(() => {});
                        } catch {}
                    }
                }
            } catch (e) {
                console.error('[Gamification] Error processing level-up rewards:', e);
            }
        }
    }

    saveGamificationState(state);

    // Also update user object in localStorage
    updateUserGamificationStats(newTotalXP, newLevel.level, state.currentStreak);

    return {
        xpGained: amount,
        previousXP,
        newTotalXP,
        leveledUp,
        previousLevel,
        newLevel,
        bonusXP,
        reason,
        levelUpReward
    };
}

/**
 * Update user object with gamification stats
 */
function updateUserGamificationStats(totalXP: number, level: number, streak: number): void {
    try {
        const storedUser = localStorage.getItem('ledger_current_user');
        if (storedUser) {
            const user = JSON.parse(storedUser);
            user.totalXP = totalXP;
            user.level = level;
            user.streak = streak;
            localStorage.setItem('ledger_current_user', JSON.stringify(user));

            // Also update users array
            const allUsers = JSON.parse(localStorage.getItem('ledger_users') || '[]');
            const updatedUsers = allUsers.map((u: any) =>
                u.id === user.id ? { ...u, totalXP, level, streak } : u
            );
            localStorage.setItem('ledger_users', JSON.stringify(updatedUsers));
        }
    } catch (e) {
        console.error('[Gamification] Error updating user stats:', e);
    }
}

// ==================== STREAK MANAGEMENT ====================

/**
 * Update daily streak
 */
export function updateStreak(): StreakResult {
    const state = loadGamificationState();
    const today = new Date().toISOString().split('T')[0];
    const lastActive = state.lastActiveDate;

    const result: StreakResult = {
        currentStreak: state.currentStreak,
        previousStreak: state.currentStreak,
        longestStreak: state.longestStreak,
        isNewDay: lastActive !== today,
        streakExtended: false,
        streakLost: false,
        streakFrozen: false,
        bonusTokens: 0
    };

    if (!result.isNewDay) {
        // Same day, no changes needed
        return result;
    }

    // Check if we're on consecutive days
    const lastDate = new Date(lastActive);
    const todayDate = new Date(today);
    const diffDays = Math.floor((todayDate.getTime() - lastDate.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays === 1) {
        // Consecutive day - extend streak
        state.currentStreak += 1;
        state.longestStreak = Math.max(state.longestStreak, state.currentStreak);
        state.streakStatus = 'active';
        state.frozenDate = null;
        result.streakExtended = true;
        result.bonusTokens = Math.min(state.currentStreak * 5, 50); // 5 tokens per day, max 50
    } else if (diffDays === 2) {
        // Missed ONE day - Freeze it!
        if (state.streakStatus !== 'frozen') {
            state.streakStatus = 'frozen';
            state.frozenDate = today;
            result.streakFrozen = true;
            // Do NOT reset streak yet
        } else {
            // Already frozen and another day passed? Break it.
            // Actually diffDays is calculated from lastActive. 
            // If lastActive was Day 1, and today is Day 3, diff is 2.
            // So this enters frozen state properly.
        }
    } else if (diffDays > 2) {
        // Streak broken (more than 1 day missed)
        result.streakLost = true;
        result.previousStreak = state.currentStreak;
        state.currentStreak = 1; // Start new streak today
        state.streakStatus = 'active';
        state.frozenDate = null;
    } else {
        // Same day (should be caught by isNewDay) or first login
        if (state.currentStreak === 0) {
            state.currentStreak = 1;
            state.streakStatus = 'active';
            result.streakExtended = true;
        }
    }

    // If it's frozen, we don't update lastActiveDate to today just yet 
    // because we want 'diffDays' to remain > 1 until they repair it?
    // NO. If we don't update lastActiveDate, diffDays will increase tomorrow to 3, causing a break.
    // That's correct behavior. Frozen lasts for 24h (until next check).

    // BUT, if we update lastActiveDate to today, then tomorrow diffDays will be 1.
    // That would mean they "kept" the streak by just logging in, effectively ignoring the gap.
    // That defeats the purpose of paying to repair.

    // Correction:
    // If Frozen, we DO NOT update lastActiveDate. We force them to repair.
    // If they don't repair, next time diffDays will be larger and break it.

    if (!result.streakFrozen) {
        state.lastActiveDate = today;
    }

    result.currentStreak = state.currentStreak;
    result.longestStreak = state.longestStreak;

    saveGamificationState(state);

    // Don't sync stats if frozen, wait for resolution
    if (!result.streakFrozen) {
        updateUserGamificationStats(state.totalXP, calculateLevel(state.totalXP).level, state.currentStreak);
    }

    return result;
}

/**
 * Repair a frozen streak
 */
export function repairStreak(): void {
    const state = loadGamificationState();
    if (state.streakStatus !== 'frozen') return;

    // Set last active date to YESTERDAY to bridge the gap
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    state.lastActiveDate = new Date().toISOString().split('T')[0]; // Set to today effectively
    // Wait, if we set to today, we just resumed it.
    // But we need to count the missed day? 
    // If we just want to continue, setting to today is fine, next login tomorrow will be diff=1.

    state.streakStatus = 'active';
    state.frozenDate = null;

    // Optionally add to streak count regarding the "missed" day?
    // Usually repairs just keep the count, maybe add 1 for "recovered" day.
    // Let's just keep the count safe.

    saveGamificationState(state);
    updateUserGamificationStats(state.totalXP, calculateLevel(state.totalXP).level, state.currentStreak);
}

/**
 * Get streak status information
 */
export function getStreakStatus(): {
    current: number;
    longest: number;
    willExpireIn: number;
    isAtRisk: boolean;
} {
    const state = loadGamificationState();
    const lastActive = new Date(state.lastActiveDate);
    const now = new Date();

    // Calculate hours until streak expires (24 hours from last active midnight)
    const lastMidnight = new Date(lastActive);
    lastMidnight.setHours(23, 59, 59, 999);
    const expiresAt = new Date(lastMidnight.getTime() + 24 * 60 * 60 * 1000);

    const hoursRemaining = Math.max(0, Math.floor((expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60)));

    return {
        current: state.currentStreak,
        longest: state.longestStreak,
        willExpireIn: hoursRemaining,
        isAtRisk: hoursRemaining < 6 && hoursRemaining > 0
    };
}

// ==================== ACHIEVEMENTS ====================

/**
 * Check and unlock achievements
 */
export function checkAchievements(): AchievementUnlock[] {
    const state = loadGamificationState();
    const unlocks: AchievementUnlock[] = [];

    for (const achievement of ACHIEVEMENTS) {
        // Skip already unlocked
        if (state.unlockedAchievements.includes(achievement.id)) {
            continue;
        }

        // Check if achievement is earned
        if (isAchievementEarned(achievement, state)) {
            state.unlockedAchievements.push(achievement.id);

            unlocks.push({
                achievement,
                xpAwarded: achievement.xpReward,
                tokensAwarded: achievement.tokenReward,
                unlockedAt: Date.now()
            });

            // Award the XP
            state.totalXP += achievement.xpReward;
        }
    }

    if (unlocks.length > 0) {
        saveGamificationState(state);
        updateUserGamificationStats(state.totalXP, calculateLevel(state.totalXP).level, state.currentStreak);
    }

    return unlocks;
}

/**
 * Check if an achievement is earned
 */
function isAchievementEarned(achievement: Achievement, state: UserGamificationState): boolean {
    const { type, metric, target } = achievement.requirement;

    switch (type) {
        case 'count':
            return getMetricValue(metric, state) >= target;
        case 'streak':
            return state.currentStreak >= target || state.longestStreak >= target;
        case 'score':
            if (metric === 'quiz_perfect') {
                return state.stats.perfectQuizzes >= target;
            }
            return false;
        case 'time':
            if (metric === 'session_minutes') {
                // This would need real-time session tracking
                return state.stats.totalStudyMinutes >= target;
            }
            return false;
        case 'special':
            return checkSpecialCondition(metric, state, target);
        default:
            return false;
    }
}

// ==================== CPALE MASTERY SYSTEM ====================

/**
 * Initialize progress for a chosen subject
 * No preliminary exam required — user goes straight to the first topic
 */
export function initializeSubjectProgress(subjectId: string): void {
    const state = loadGamificationState();

    if (!state.subjectProgress) state.subjectProgress = {};

    const subject = CPALE_SYLLABUS.find(s => s.id === subjectId);
    if (!subject) return;

    const firstTopic = subject.topics.find(t => t.order === 1);
    if (!firstTopic) return;

    // Always update selected subject
    state.selectedSubject = subjectId;

    // Initialize progress entry if missing
    if (!state.subjectProgress[subjectId]) {
        state.subjectProgress[subjectId] = {
            isPreliminaryPassed: true, // No prelim exam needed — go straight to topics
            currentTopicId: firstTopic.id,
            topicXP: {},
            completedTopics: []
        };
    } else {
        // Ensure existing progress has prelim passed (migration)
        state.subjectProgress[subjectId].isPreliminaryPassed = true;
    }

    saveGamificationState(state);
}

/**
 * Award Mastery Points to a specific topic
 * Called when the user has a relevant conversation with the AI about the current topic
 */
export function awardTopicXP(subjectId: string, topicId: string, amount: number): { newPoints: number; thresholdReached: boolean } {
    const state = loadGamificationState();

    if (!state.subjectProgress || !state.subjectProgress[subjectId]) {
        return { newPoints: 0, thresholdReached: false };
    }

    const progress = state.subjectProgress[subjectId];

    // Only award points to the current topic (not completed or locked ones)
    if (progress.completedTopics.includes(topicId)) {
        return { newPoints: progress.topicXP?.[topicId] || 0, thresholdReached: false };
    }

    // Initialize topic XP if missing
    if (!progress.topicXP) progress.topicXP = {};

    const oldPoints = progress.topicXP[topicId] || 0;
    const newPoints = oldPoints + amount;
    progress.topicXP[topicId] = newPoints;

    saveGamificationState(state);

    // Also award global XP
    awardXP(amount, 'Mastery Study');

    // Check if threshold reached
    const subject = CPALE_SYLLABUS.find(s => s.id === subjectId);
    const topic = subject?.topics.find(t => t.id === topicId);
    const threshold = topic?.masteryPointsRequired || 200;
    const thresholdReached = newPoints >= threshold && oldPoints < threshold;

    return { newPoints, thresholdReached };
}

/**
 * Mark Preliminary Exam as Passed
 */
export function passPreliminaryExam(subjectId: string): void {
    const state = loadGamificationState();
    if (!state.subjectProgress?.[subjectId]) return;

    state.subjectProgress[subjectId].isPreliminaryPassed = true;
    saveGamificationState(state);

    // Publish Activity
    try {
        const user = JSON.parse(localStorage.getItem('ledger_current_user') || '{}');
        if (user.id && user.username) {
            publishActivity(
                user.id,
                user.username,
                user.accountTier || 'free',
                'master_subject',
                `Unlocked ${subjectId.toUpperCase()} Mastery Path`,
                'Passed the diagnostic exam!'
            );
        }
    } catch (e) { console.error('Error publishing activity', e); }
}


/**
 * Get the current mastery path state for the active subject
 */
export function getMasteryPathState(): {
    selectedSubject: string | null;
    currentTopicId: string | null;
    topicPoints: Record<string, number>;
    completedTopics: string[];
} {
    const state = loadGamificationState();
    const subjectId = state.selectedSubject;
    if (!subjectId || !state.subjectProgress?.[subjectId]) {
        return { selectedSubject: null, currentTopicId: null, topicPoints: {}, completedTopics: [] };
    }
    const progress = state.subjectProgress[subjectId];
    return {
        selectedSubject: subjectId,
        currentTopicId: progress.currentTopicId,
        topicPoints: progress.topicXP || {},
        completedTopics: progress.completedTopics || []
    };
}

/**
 * Switch the active subject (user can switch freely between subjects)
 */
export function switchSubject(subjectId: string): void {
    const state = loadGamificationState();
    state.selectedSubject = subjectId;
    
    // Initialize if not yet started
    if (!state.subjectProgress) state.subjectProgress = {};
    if (!state.subjectProgress[subjectId]) {
        const subject = CPALE_SYLLABUS.find(s => s.id === subjectId);
        const firstTopic = subject?.topics.find(t => t.order === 1);
        if (firstTopic) {
            state.subjectProgress[subjectId] = {
                isPreliminaryPassed: true,
                currentTopicId: firstTopic.id,
                topicXP: {},
                completedTopics: []
            };
        }
    }
    
    saveGamificationState(state);
}

/**
 * Unlock Next Topic (Phase 3.1)
 */
export function unlockNextTopic(subjectId: string, currentTopicId: string): void {
    const state = loadGamificationState();
    if (!state.subjectProgress?.[subjectId]) return;

    const progress = state.subjectProgress[subjectId];

    // Already completed?
    if (progress.completedTopics.includes(currentTopicId)) return;

    // Mark current as completed
    progress.completedTopics.push(currentTopicId);

    // Find next topic
    // Synchronous find using constant
    const subject = CPALE_SYLLABUS.find(s => s.id === subjectId);
    if (!subject) {
        saveGamificationState(state);
        return;
    }

    const currentIndex = subject.topics.findIndex(t => t.id === currentTopicId);
    if (currentIndex === -1) {
        saveGamificationState(state);
        return;
    }

    if (currentIndex < subject.topics.length - 1) {
        // Unlock next
        const nextTopic = subject.topics[currentIndex + 1];
        progress.currentTopicId = nextTopic.id;
    }

    saveGamificationState(state);

    // Award Completion Bonus
    awardXP(2000, `Completed Topic: ${subject.topics[currentIndex].title}`);

    // Publish Activity
    try {
        const user = JSON.parse(localStorage.getItem('ledger_current_user') || '{}');
        if (user.id && user.username) {
            publishActivity(
                user.id,
                user.username,
                user.accountTier || 'free',
                'master_topic',
                `Mastered ${subject.topics[currentIndex].title}`,
                `Scored 75%+ on the mastery exam.`
            );
        }
    } catch (e) { console.error('Error publishing activity', e); }
}

// ==================== CHECKLIST-BASED PROGRESSION ====================

/**
 * Complete a checklist item for a topic.
 * Returns true if all checklist items are now complete.
 */
export function completeChecklistItem(subjectId: string, topicId: string, checklistItemId: string): boolean {
    const state = loadGamificationState();
    if (!state.subjectProgress?.[subjectId]) return false;

    const progress = state.subjectProgress[subjectId];

    // Initialize checklist progress if missing
    if (!progress.checklistProgress) progress.checklistProgress = {};
    if (!progress.checklistProgress[topicId]) progress.checklistProgress[topicId] = [];

    // Don't double-complete
    if (progress.checklistProgress[topicId].includes(checklistItemId)) return false;

    progress.checklistProgress[topicId].push(checklistItemId);
    saveGamificationState(state);

    // Award XP for completing a checklist item
    awardXP(50, `Checklist: ${checklistItemId}`);

    // Check if all checklist items are done
    const subject = CPALE_SYLLABUS.find(s => s.id === subjectId);
    const topic = subject?.topics.find(t => t.id === topicId);
    if (!topic) return false;

    const allDone = topic.checklist.every(cl => progress.checklistProgress![topicId].includes(cl.id));
    return allDone;
}

/**
 * Get completed checklist items for a topic
 */
export function getCompletedChecklist(subjectId: string, topicId: string): string[] {
    const state = loadGamificationState();
    return state.subjectProgress?.[subjectId]?.checklistProgress?.[topicId] || [];
}

/**
 * Save a written note for a topic (required before taking the mastery exam)
 */
export function saveTopicNote(subjectId: string, topicId: string, note: string): void {
    const state = loadGamificationState();
    if (!state.subjectProgress?.[subjectId]) return;

    const progress = state.subjectProgress[subjectId];
    if (!progress.topicNotes) progress.topicNotes = {};
    progress.topicNotes[topicId] = {
        content: note,
        savedAt: Date.now()
    };

    saveGamificationState(state);
}

/**
 * Get the saved note for a topic
 */
export function getTopicNote(subjectId: string, topicId: string): { content: string; savedAt: number } | null {
    const state = loadGamificationState();
    return state.subjectProgress?.[subjectId]?.topicNotes?.[topicId] || null;
}

/**
 * Check if user can take the mastery exam for a topic.
 * New logic: all checklist items must be completed AND a note must be written.
 * Falls back to legacy points check if checklist data is missing.
 */
export function canTakeTopicExam(subjectId: string, topicId: string, requiredPoints?: number): boolean {
    const state = loadGamificationState();
    const progress = state.subjectProgress?.[subjectId];
    if (!progress) return false;

    // New: checklist-based check
    const subject = CPALE_SYLLABUS.find(s => s.id === subjectId);
    const topic = subject?.topics.find(t => t.id === topicId);
    if (!topic) return false;

    // Check all checklist items completed
    const completedItems = progress.checklistProgress?.[topicId] || [];
    const allChecklistDone = topic.checklist.every(cl => completedItems.includes(cl.id));

    // Check note written (if required)
    const noteWritten = !topic.requiresNote || !!(progress.topicNotes?.[topicId]?.content);

    if (allChecklistDone && noteWritten) return true;

    // Legacy fallback: points-based
    const currentPoints = progress.topicXP?.[topicId] || 0;
    const threshold = requiredPoints ?? topic.masteryPointsRequired ?? 200;
    return currentPoints >= threshold;
}

/**
 * Record a failed exam attempt and generate remedial checklist items based on mistakes.
 * Returns the IDs of the new remedial checklist items that must be completed before retaking.
 */
export function recordFailedExamAttempt(
    subjectId: string,
    topicId: string,
    wrongQuestions: Array<{ question: string; correctAnswer: string; userAnswer: string; topic?: string }>
): string[] {
    const state = loadGamificationState();
    if (!state.subjectProgress?.[subjectId]) return [];

    const progress = state.subjectProgress[subjectId];

    // Track attempt count
    if (!progress.examAttempts) progress.examAttempts = {};
    progress.examAttempts[topicId] = (progress.examAttempts[topicId] || 0) + 1;
    const attemptNum = progress.examAttempts[topicId];

    // Generate remedial checklist items based on wrong answers
    if (!progress.remedialChecklist) progress.remedialChecklist = {};
    const remedialItems: string[] = [];

    // Create a review item for each wrong question (max 5)
    const wrongSlice = wrongQuestions.slice(0, 5);
    wrongSlice.forEach((wq, i) => {
        const itemId = `${topicId}-remedial-${attemptNum}-${i}`;
        remedialItems.push(itemId);
    });

    // Add a remedial mini-quiz
    const quizItemId = `${topicId}-remedial-quiz-${attemptNum}`;
    remedialItems.push(quizItemId);

    progress.remedialChecklist[topicId] = {
        attemptNumber: attemptNum,
        items: remedialItems,
        completedItems: [],
        wrongQuestions: wrongSlice
    };

    saveGamificationState(state);
    return remedialItems;
}

/**
 * Complete a remedial checklist item
 */
export function completeRemedialItem(subjectId: string, topicId: string, itemId: string): boolean {
    const state = loadGamificationState();
    const progress = state.subjectProgress?.[subjectId];
    if (!progress?.remedialChecklist?.[topicId]) return false;

    const remedial = progress.remedialChecklist[topicId];
    if (!remedial.completedItems.includes(itemId)) {
        remedial.completedItems.push(itemId);
    }

    saveGamificationState(state);

    // Return true if all remedial items are complete
    return remedial.items.every(id => remedial.completedItems.includes(id));
}

/**
 * Get remedial checklist state for a topic (null if no failed attempts)
 */
export function getRemedialChecklist(subjectId: string, topicId: string): {
    attemptNumber: number;
    items: string[];
    completedItems: string[];
    wrongQuestions: Array<{ question: string; correctAnswer: string; userAnswer: string }>;
    allComplete: boolean;
} | null {
    const state = loadGamificationState();
    const remedial = state.subjectProgress?.[subjectId]?.remedialChecklist?.[topicId];
    if (!remedial) return null;

    return {
        ...remedial,
        allComplete: remedial.items.every(id => remedial.completedItems.includes(id))
    };
}

/**
 * Check if user can retake the exam after failing (all remedial items must be done)
 */
export function canRetakeExam(subjectId: string, topicId: string): boolean {
    const remedial = getRemedialChecklist(subjectId, topicId);
    if (!remedial) return true; // No failed attempts, can take normally
    return remedial.allComplete;
}

/**
 * Get metric value from state
 */
function getMetricValue(metric: string, state: UserGamificationState): number {
    switch (metric) {
        case 'questions_asked':
            return state.stats.totalQuestions;
        case 'quizzes_completed':
            return state.stats.totalQuizzes;
        case 'sessions_started':
            return state.stats.totalSessions;
        case 'referrals':
            try {
                const user = JSON.parse(localStorage.getItem('ledger_current_user') || '{}');
                return user.referralCount || 0;
            } catch { return 0; }
        case 'purchases':
            try {
                const user = JSON.parse(localStorage.getItem('ledger_current_user') || '{}');
                return user.purchaseCount || user.totalTokensPurchased ? 1 : 0;
            } catch { return 0; }
        case 'backgrounds_unlocked':
            try {
                const user = JSON.parse(localStorage.getItem('ledger_current_user') || '{}');
                return (user.unlockedBackgrounds || []).length;
            } catch { return 0; }
        case 'far_questions':
            return state.stats.domainQuestions['far'] || 0;
        case 'tax_questions':
            return state.stats.domainQuestions['tax'] || 0;
        case 'aud_questions':
            return state.stats.domainQuestions['aud'] || 0;
        default:
            return 0;
    }
}

/**
 * Check special achievement conditions
 */
function checkSpecialCondition(metric: string, state: UserGamificationState, target: number): boolean {
    switch (metric) {
        case 'all_domains':
            return Object.keys(state.stats.domainQuestions).length >= target;
        case 'night_study': {
            const hour = new Date().getHours();
            return (hour >= 0 && hour < 4) && state.stats.totalSessions > 0;
        }
        case 'early_study': {
            const hour = new Date().getHours();
            return hour < 6 && state.stats.totalSessions > 0;
        }
        case 'premium':
            try {
                const user = JSON.parse(localStorage.getItem('ledger_current_user') || '{}');
                return user.accountType === 'premium' || user.isPremium;
            } catch { return false; }

        case 'pass_prelim_aud':
            return !!state.subjectProgress?.['aud']?.isPreliminaryPassed;
        case 'pass_prelim_tax':
            return !!state.subjectProgress?.['tax']?.isPreliminaryPassed;
        case 'pass_prelim_far':
            return !!state.subjectProgress?.['far']?.isPreliminaryPassed;
        case 'pass_prelim_rfbt':
            return !!state.subjectProgress?.['rfbt']?.isPreliminaryPassed;
        case 'pass_prelim_mas':
            return !!state.subjectProgress?.['mas']?.isPreliminaryPassed;
        case 'pass_prelim_afar':
            return !!state.subjectProgress?.['afar']?.isPreliminaryPassed;
        case 'max':
            try {
                const user = JSON.parse(localStorage.getItem('ledger_current_user') || '{}');
                return user.accountType === 'max' || user.isMax;
            } catch { return false; }
        case 'reach_level': {
            const currentLevel = calculateLevel(state.totalXP);
            return currentLevel.level >= target;
        }
        case 'weekend_study': {
            const day = new Date().getDay();
            return (day === 0 || day === 6) && state.dailyProgress.questionsAsked > 0;
        }
        case 'rapid_questions':
            return state.dailyProgress.questionsAsked >= target;
        case 'all_backgrounds':
            try {
                const user = JSON.parse(localStorage.getItem('ledger_current_user') || '{}');
                return (user.unlockedBackgrounds || []).length >= 10;
            } catch { return false; }
        default:
            return false;
    }
}

/**
 * Get achievement progress for display
 */
export function getAchievementProgress(achievementId: string): { current: number; target: number; percent: number } {
    const state = loadGamificationState();
    const achievement = ACHIEVEMENTS.find(a => a.id === achievementId);

    if (!achievement) {
        return { current: 0, target: 100, percent: 0 };
    }

    const { type, metric, target } = achievement.requirement;
    let current = 0;

    switch (type) {
        case 'count':
            current = getMetricValue(metric, state);
            break;
        case 'streak':
            current = Math.max(state.currentStreak, state.longestStreak);
            break;
        case 'score':
            if (metric === 'quiz_perfect') {
                current = state.stats.perfectQuizzes;
            }
            break;
        case 'time':
            current = state.stats.totalStudyMinutes;
            break;
        case 'special':
            current = checkSpecialCondition(metric, state, target) ? target : 0;
            break;
    }

    return {
        current,
        target,
        percent: Math.min(Math.round((current / target) * 100), 100)
    };
}

// ==================== DAILY CHALLENGES ====================

/**
 * Get current daily challenges with progress
 */
export function getDailyChallenges(): DailyChallengeStatus[] {
    const state = loadGamificationState();

    return state.dailyChallenges.challenges.map(challenge => {
        const progress = getChallengeProgress(challenge, state);
        const completed = progress >= challenge.target;
        const claimedReward = state.dailyChallenges.completedIds.includes(challenge.id);

        return {
            challenge,
            progress,
            completed,
            claimedReward
        };
    });
}

/**
 * Get progress for a specific challenge
 */
function getChallengeProgress(challenge: DailyChallenge, state: UserGamificationState): number {
    const progress = state.dailyProgress;

    switch (challenge.type) {
        case 'questions':
            return progress.questionsAsked;
        case 'quizzes':
            return progress.quizzesCompleted;
        case 'time':
            return progress.studyMinutes;
        case 'domain':
            return progress.domainsStudied.length;
        case 'streak':
            return state.currentStreak > 0 ? 1 : 0;
        default:
            return state.dailyChallenges.progress[challenge.id] || 0;
    }
}

/**
 * Complete a daily challenge and claim reward
 */
export function completeDailyChallenge(challengeId: string): {
    success: boolean;
    tokensAwarded: number;
    xpAwarded: number;
} {
    const state = loadGamificationState();

    // Check if already completed
    if (state.dailyChallenges.completedIds.includes(challengeId)) {
        return { success: false, tokensAwarded: 0, xpAwarded: 0 };
    }

    const challenge = state.dailyChallenges.challenges.find(c => c.id === challengeId);
    if (!challenge) {
        return { success: false, tokensAwarded: 0, xpAwarded: 0 };
    }

    // Check if actually completed
    const progress = getChallengeProgress(challenge, state);
    if (progress < challenge.target) {
        return { success: false, tokensAwarded: 0, xpAwarded: 0 };
    }

    // Mark as completed
    state.dailyChallenges.completedIds.push(challengeId);

    // Award XP
    state.totalXP += challenge.xpReward;

    saveGamificationState(state);
    updateUserGamificationStats(state.totalXP, calculateLevel(state.totalXP).level, state.currentStreak);

    return {
        success: true,
        tokensAwarded: challenge.tokenReward,
        xpAwarded: challenge.xpReward
    };
}

// ==================== MILESTONES ====================

/**
 * Check and unlock milestones
 */
export function checkMilestones(): MilestoneResult[] {
    const state = loadGamificationState();
    const results: MilestoneResult[] = [];

    for (const milestone of PROGRESS_MILESTONES) {
        // Skip already reached
        if (state.unlockedMilestones.includes(milestone.id)) {
            continue;
        }

        // Check if milestone is reached
        if (isMilestoneReached(milestone, state)) {
            state.unlockedMilestones.push(milestone.id);
            state.totalXP += milestone.xpReward;

            results.push({
                milestone,
                justReached: true,
                tokensAwarded: milestone.tokenReward,
                xpAwarded: milestone.xpReward
            });
        }
    }

    if (results.length > 0) {
        saveGamificationState(state);
        updateUserGamificationStats(state.totalXP, calculateLevel(state.totalXP).level, state.currentStreak);
    }

    return results;
}

/**
 * Check if a milestone is reached
 */
function isMilestoneReached(milestone: ProgressMilestone, state: UserGamificationState): boolean {
    switch (milestone.metric) {
        case 'sessions_completed':
            return state.stats.totalSessions >= milestone.threshold;
        case 'total_questions':
            return state.stats.totalQuestions >= milestone.threshold;
        case 'total_study_minutes':
            return state.stats.totalStudyMinutes >= milestone.threshold;
        case 'quizzes_completed':
            return state.stats.totalQuizzes >= milestone.threshold;
        default:
            return false;
    }
}

// ==================== TRACKING FUNCTIONS ====================

/**
 * Track a question asked
 */
export function trackQuestion(domain?: string): void {
    const state = loadGamificationState();

    state.stats.totalQuestions += 1;
    state.dailyProgress.questionsAsked += 1;

    if (domain) {
        const normalizedDomain = domain.toLowerCase();
        state.stats.domainQuestions[normalizedDomain] =
            (state.stats.domainQuestions[normalizedDomain] || 0) + 1;

        if (!state.dailyProgress.domainsStudied.includes(normalizedDomain)) {
            state.dailyProgress.domainsStudied.push(normalizedDomain);
        }
    }

    saveGamificationState(state);
}

/**
 * Track quiz completion
 */
export function trackQuizComplete(passed: boolean, score: number): void {
    const state = loadGamificationState();

    state.stats.totalQuizzes += 1;
    state.dailyProgress.quizzesCompleted += 1;

    if (passed) {
        state.stats.quizzesPassed += 1;
    }

    if (score === 100) {
        state.stats.perfectQuizzes += 1;
    }

    saveGamificationState(state);
}

/**
 * Track study time
 */
export function trackStudyTime(minutes: number): void {
    const state = loadGamificationState();

    state.stats.totalStudyMinutes += minutes;
    state.dailyProgress.studyMinutes += minutes;

    saveGamificationState(state);
}

/**
 * Track new session
 */
export function trackSession(): void {
    const state = loadGamificationState();

    state.stats.totalSessions += 1;
    state.dailyProgress.sessionsStarted += 1;

    saveGamificationState(state);
}

// ==================== SUMMARY ====================

/**
 * Get gamification summary for display
 */
export function getGamificationSummary(): {
    xp: { total: number; today: number };
    level: LevelConfig;
    progress: { current: number; needed: number; percent: number };
    streak: { current: number; longest: number };
    achievements: { unlocked: number; total: number };
    challenges: { completed: number; total: number };
} {
    const state = loadGamificationState();
    const level = calculateLevel(state.totalXP);
    const progress = xpToNextLevel(state.totalXP);

    // Calculate today's XP (simplified - based on daily progress)
    const todayXP =
        state.dailyProgress.questionsAsked * 3 +
        state.dailyProgress.quizzesCompleted * 25 +
        Math.floor(state.dailyProgress.studyMinutes / 5) * 5;

    return {
        xp: {
            total: state.totalXP,
            today: todayXP
        },
        level,
        progress: {
            current: progress.current,
            needed: progress.needed,
            percent: progress.progress
        },
        streak: {
            current: state.currentStreak,
            longest: state.longestStreak
        },
        achievements: {
            unlocked: state.unlockedAchievements.length,
            total: ACHIEVEMENTS.length
        },
        challenges: {
            completed: state.dailyChallenges.completedIds.length,
            total: state.dailyChallenges.challenges.length
        }
    };
}

// ==================== EXPORTS ====================

export {
    calculateLevel,
    xpToNextLevel,
    LEVEL_CONFIGS,
    ACHIEVEMENTS,
    DAILY_CHALLENGES,
    PROGRESS_MILESTONES
};

export type {
    LevelConfig,
    Achievement,
    DailyChallenge,
    ProgressMilestone
};

/**
 * Contextual Insights for Smart Notifications
 */
export interface ContextualInsights {
    xpToNextLevel: number;
    currentLevel: number;
    nextLevel: number;
    currentStreak: number;
    daysInactive: number;
    tokens: number; // For "low balance" checks, though strictly user data
    nextMilestone?: ProgressMilestone;
    nearestAchievement?: Achievement;
}

/**
 * Get contextual insights for notifications
 */
export function getContextualInsights(): ContextualInsights {
    const state = loadGamificationState();
    const currentXP = state.totalXP;
    const { level } = calculateLevel(currentXP);
    const xpInfo = xpToNextLevel(currentXP);
    const xpNeeded = Math.max(0, xpInfo.needed - xpInfo.current);

    // Calculate days inactive
    const lastActive = new Date(state.lastActiveDate);
    // Normalize to midnight to avoid hour differences affecting "days"
    lastActive.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffTime = Math.abs(today.getTime() - lastActive.getTime());
    const daysInactive = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    // Find nearest milestone
    const nextMilestone = PROGRESS_MILESTONES.find(m => !state.unlockedMilestones.includes(m.id));

    return {
        xpToNextLevel: xpNeeded,
        currentLevel: level,
        nextLevel: level + 1,
        currentStreak: state.currentStreak,
        daysInactive,
        tokens: 0, // We can't access user tokens directly here without circular dep or extra read. 
        // Notification service should fetch user token balance separately if needed.
        nextMilestone,
        // strategies for finding nearest achievement could be complex, omitting for MVP speed
    };
}

/**
 * Unlock a mastery node
 */
export async function unlockMasteryNode(nodeId: string): Promise<void> {
    const state = loadGamificationState();
    if (!state.masteryProgress) return;

    if (!state.masteryProgress.unlockedNodes.includes(nodeId)) {
        const newState = {
            ...state,
            masteryProgress: {
                ...state.masteryProgress,
                unlockedNodes: [...state.masteryProgress.unlockedNodes, nodeId]
            }
        };
        await saveGamificationState(newState);
    }
}

/**
 * Complete a mastery node and record score
 */
export async function completeMasteryNode(nodeId: string, score: number, nextNodeIds: string[] = []): Promise<void> {
    const state = loadGamificationState();
    if (!state.masteryProgress) return;

    let newState = { ...state };
    const progress = { ...newState.masteryProgress! };

    // Mark completed
    if (!progress.completedNodes.includes(nodeId)) {
        progress.completedNodes = [...progress.completedNodes, nodeId];
    }

    // Update score if higher
    const currentScore = progress.nodeScores[nodeId] || 0;
    if (score > currentScore) {
        progress.nodeScores = {
            ...progress.nodeScores,
            [nodeId]: score
        };
    }

    // Unlock next nodes if passed
    if (score >= 70) { // Passing score
        const newUnlocks = nextNodeIds.filter(id => !progress.unlockedNodes.includes(id));
        if (newUnlocks.length > 0) {
            progress.unlockedNodes = [...progress.unlockedNodes, ...newUnlocks];

            // Advance current node pointer if following linear path
            if (newUnlocks.length === 1) {
                progress.currentNodeId = newUnlocks[0];
            }
        }
    }

    newState.masteryProgress = progress;
    await saveGamificationState(newState);
}

// ==================== ASSESSMENT TRACKING ====================

/**
 * Save detailed quiz history
 */
export async function saveQuizResult(userId: string, result: QuizAttempt): Promise<void> {
    if (!db) return;

    try {
        // Update Firestore
        const userRef = doc(db, 'users', userId);
        await updateDoc(userRef, {
            quizHistory: arrayUnion(result),
            quizAttempts: increment(1)
        });

        // Update Local User
        const storedUser = localStorage.getItem('ledger_current_user');
        if (storedUser) {
            const user = JSON.parse(storedUser);
            user.quizHistory = [...(user.quizHistory || []), result];
            user.quizAttempts = (user.quizAttempts || 0) + 1;
            localStorage.setItem('ledger_current_user', JSON.stringify(user));
        }

        // Update Gamification Stats
        const state = loadGamificationState();
        state.stats.totalQuizzes += 1;
        state.stats.quizzesPassed += (result.percentage >= 75 ? 1 : 0);
        state.dailyProgress.quizzesCompleted += 1;
        saveGamificationState(state);

    } catch (e) {
        console.error('[Gamification] Error saving quiz result:', e);
    }
}

// ==================== STUDY GUIDE PROGRESS ====================

/**
 * Mark a study guide phase as complete for a topic.
 * Persists to localStorage + Firebase via gamification state.
 */
export function markGuidePhaseComplete(topicId: string, phaseId: string): Record<string, string[]> {
    const state = loadGamificationState();
    if (!state.guideProgress) state.guideProgress = {};
    if (!state.guideProgress[topicId]) state.guideProgress[topicId] = [];
    if (!state.guideProgress[topicId].includes(phaseId)) {
        state.guideProgress[topicId].push(phaseId);
    }
    saveGamificationState(state);
    return state.guideProgress;
}

/**
 * Check if a guide phase is complete
 */
export function isGuidePhaseComplete(topicId: string, phaseId: string): boolean {
    const state = loadGamificationState();
    return state.guideProgress?.[topicId]?.includes(phaseId) || false;
}

/**
 * Get all completed phases for a topic
 */
export function getGuideProgress(): Record<string, string[]> {
    const state = loadGamificationState();
    return state.guideProgress || {};
}

/**
 * Save mock exam history
 */
export async function saveMockExamResult(userId: string, result: MockExamAttempt): Promise<void> {
    if (!db) return;

    try {
        // Update Firestore
        const userRef = doc(db, 'users', userId);
        await updateDoc(userRef, {
            mockExamHistory: arrayUnion(result)
        });

        // Update Local User
        const storedUser = localStorage.getItem('ledger_current_user');
        if (storedUser) {
            const user = JSON.parse(storedUser);
            user.mockExamHistory = [...(user.mockExamHistory || []), result];
            localStorage.setItem('ledger_current_user', JSON.stringify(user));
        }

    } catch (e) {
        console.error('[Gamification] Error saving mock exam result:', e);
    }
}
